import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Message as MessageIcon,
  AutoAwesome as AutoReplyIcon,
  SmartToy as ChatGptIcon,
  WarningAmber as WarningAmberIcon,
  Link as LinkIcon,
  Bolt as BoltIcon,
  People as PeopleIcon,
  Forum as ForumIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalMessages: number;
  totalAutoReplies: number;
  totalPrompts: number;
  totalComments?: number;
  totalContacts?: number;
}

const Dashboard: React.FC = () => {
  const { user, updateSubscriptionLocally } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Connected account check
  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;

  // On billing_success, verify the Stripe session on backend and refresh auth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingSuccess = params.get('billing_success');
    if (billingSuccess === '1') {
      const sessionId = params.get('session_id');
      const planRaw = (typeof window !== 'undefined' ? sessionStorage.getItem('selectedPlan') : null) || '';
      const normalized = planRaw.toLowerCase();
      const map: Record<string, string> = { starter: 'starter', pro: 'professional', professional: 'professional', business: 'enterprise', enterprise: 'enterprise' };
      const mapped = map[normalized as keyof typeof map];

      const verify = async () => {
        try {
          await axios.post('/api/billing/verify', { session_id: sessionId, plan: mapped }, { withCredentials: true });
          // Refresh auth to fetch updated user from DB
          try { await (window as any).checkAuth?.(); } catch {}
        } catch (e) {
          console.error('Billing verify failed:', e);
        } finally {
          if (typeof window !== 'undefined') sessionStorage.removeItem('selectedPlan');
        }
      };
      verify();
      const url = new URL(window.location.href);
      url.searchParams.delete('billing_success');
      window.history.replaceState({}, '', url.toString());
    }
  }, [updateSubscriptionLocally]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/dashboard/stats');
        setStats(response.data);
        setError('');
      } catch (err) {
        setError('Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };

    if (!noConnectedAccounts) {
      fetchStats();
    } else {
      setLoading(false);
      setStats(null);
      setError('');
    }
  }, [noConnectedAccounts]);

  if (loading && !noConnectedAccounts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !noConnectedAccounts) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const statCards = [
    {
      title: 'Contacts',
      value: stats?.totalContacts || 0,
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: 'warning.main',
    },
    {
      title: 'Total Messages',
      value: stats?.totalMessages || 0,
      icon: <ForumIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: 'primary.main',
    },
    {
      title: 'Agent Prompts',
      value: stats?.totalPrompts || 0,
      icon: <ChatGptIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: 'success.main',
    },
    {
      title: 'Total Comments',
      value: stats?.totalComments || 0,
      icon: <MessageIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      color: 'secondary.main',
    },
  ];

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (!user?.isTrialing || !user?.trialEndDate) return 0;
    const now = new Date();
    const trialEnd = new Date(user.trialEndDate);
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysRemaining = getTrialDaysRemaining();

  return (
    <Box sx={{ position: 'relative' }}>
      {!noConnectedAccounts && (
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 2 }}
          className="guide-welcome"
        >
          Welcome to your Social Media AI Agent dashboard. Here's an overview of your AI agent activity.
        </Typography>
      )}

      {/* Trial Status Banner */}
      {user?.isTrialing && user?.subscriptionStatus === 'trialing' && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            '& .MuiAlert-icon': {
              color: '#10B981',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669', mb: 0.5 }}>
                🎉 Your 7-Day Free Trial is Active!
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                You have <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> remaining in your trial. 
                {trialDaysRemaining <= 2 && ' Your subscription will automatically start after the trial period ends.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/app/settings')}
              sx={{
                background: 'linear-gradient(135deg, #10B981, #059669)',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669, #047857)',
                },
              }}
            >
              Manage Subscription
            </Button>
          </Box>
        </Alert>
      )}

      {noConnectedAccounts && (
        <Alert severity="warning" icon={false} sx={{ mb: 3, backgroundColor: '#FFF7E6', border: '1px solid #FFE1A3', color: '#8A6D3B', py: 1.25, px: 1.5, '& .MuiAlert-message': { p: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#F57C00' }} />
              <Typography sx={{ fontWeight: 500 }}>
                No connected accounts. Connect at least one account to see your dashboard stats.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600, py: 0.5, px: 1.5 }}
            >
              CONNECT ACCOUNT
            </Button>
          </Box>
        </Alert>
      )}

      <Box 
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}
        className="guide-dashboard"
      >
        {statCards.map((card) => (
          <Card
            key={card.title}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              ...(noConnectedAccounts ? { filter: 'blur(1.2px)', pointerEvents: 'none', userSelect: 'none' } : {}),
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
              <Box sx={{ mb: 2 }}>{card.icon}</Box>
              <Typography variant="h3" component="div" sx={{ color: card.color, mb: 1 }}>
                {card.value}
              </Typography>
              <Typography variant="h6" component="div" color="text.secondary">
                {card.title}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Quick Actions - always visible */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Quick Actions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Explore key areas to get the most out of your AI agent.
        </Typography>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}>
          {/* Contacts card first */}
          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>Contacts</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              View saved contacts from your conversations and recent interactions.
            </Typography>
            <Box sx={{ mt: 'auto' }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/app/contacts')}>Go to Contacts</Button>
            </Box>
          </Card>

          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ForumIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>Messages</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              View all conversations and manage replies across connected channels.
            </Typography>
            <Box sx={{ mt: 'auto' }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/app/messages')}>Go to Messages</Button>
            </Box>
          </Card>

          {/* Temporarily hidden */}
          {/* <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoReplyIcon color="secondary" />
              <Typography variant="subtitle1" fontWeight={600}>Auto Replies</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Create intelligent rules to automatically respond to frequent inquiries.
            </Typography>
            <Box sx={{ mt: 'auto' }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/app/autoreplies')}>Manage Auto Replies</Button>
            </Box>
          </Card> */}

          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatGptIcon color="success" />
              <Typography variant="subtitle1" fontWeight={600}>Agent Prompts</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Fine-tune the AI with reusable prompts and test them instantly.
            </Typography>
            <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/app/chatgpt-prompts')}>Manage Prompts</Button>
              <Button startIcon={<TestIcon />} variant="text" size="small" onClick={() => navigate('/app/prompt-tester')}>Prompt Tester</Button>
            </Box>
          </Card>

          <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 170 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon sx={{ color: 'secondary.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>Comments</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Review comments and set keyword‑based auto‑replies for your posts.
            </Typography>
            <Box sx={{ mt: 'auto' }}>
              <Button variant="outlined" size="small" onClick={() => navigate('/app/comments')}>Go to Comments</Button>
            </Box>
          </Card>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <BoltIcon fontSize="small" />
          <Typography variant="caption">
            Tip: You can enable Agent Response from the Messages page header to automate replies with your prompts.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard; 